import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { TeamMember as TeamMemberType } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { Linkedin, Twitter, Mail } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MapPin } from "lucide-react";

interface TeamMember {
  id: number;
  name: string;
  position: string;
  experience: string;
  image: string;
  imageUrl?: string;
  linkedinUrl: string;
  twitterUrl: string;
  email: string;
  specialization?: string;
}

// Team data is now managed directly from variables to avoid API 500 errors

export default function TeamSection() {
  const teamMembers = [
    {
      id: 1,
      name: "Sushma Upadhyay",
      position: "HR Manager",
      bio: "Expert in human resources and administrative operations with extensive experience in team management.",
      image: "/team/sushma-upadhyay.png",
      experience: "8 years",
      specialization: "Human Resources",
      email: "hr@travelwingsonline.in",
    },
    {
      id: 2,
      name: "Barkha Kumari",
      position: "Manager",
      bio: "Customer support specialist with comprehensive experience in travel operations management.",
      image: "/team/barkha-kumari-actual.png",
      experience: "5 years",
      specialization: "Customer Support",
      email: "support@travelwingsonline.in",
    },
    {
      id: 3,
      name: "Ankit Sharma",
      position: "Operation Head",
      bio: "Seasoned operations leader with comprehensive experience in travel industry management.",
      image: "/team/ankit-sharma.png",
      experience: "18 years",
      specialization: "Operations Management",
      email: "ankit@travelwingsonline.in",
    },
    {
      id: 4,
      name: "Anil Bharti",
      position: "Branch Head Vasant Kunj",
      bio: "Branch management expert with extensive experience in strategic planning and operations.",
      image: "/team/anil-bharti.png",
      experience: "25 years",
      specialization: "Branch Management",
      email: "anil@travelwingsonline.in",
    },
    {
      id: 5,
      name: "Veer Bahadur Singh",
      position: "Passport & Visa Specialist",
      bio: "Specialist in passport and visa services with extensive knowledge of international documentation.",
      image: "/team/veer-bahadur-singh.png",
      experience: "16 years",
      specialization: "Documentation Services",
      email: "veer@travelwingsonline.in",
    },
    {
      id: 6,
      name: "Meeta Arora",
      position: "DGM",
      bio: "Deputy General Manager with expertise in strategic operations and business development.",
      image: "/team/meeta-arora.png",
      experience: "16 years",
      specialization: "General Management",
      email: "meeta@travelwingsonline.in",
    },
  ];

  const isLoading = false;

  if (isLoading) {
    return (
      <section id="team" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Our Expert Team
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Loading our amazing team...
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="text-center group animate-pulse">
                <div className="w-32 h-32 bg-gray-300 rounded-full mx-auto mb-6"></div>
                <div className="h-6 bg-gray-300 rounded mb-2 mx-auto w-32"></div>
                <div className="h-4 bg-gray-300 rounded mb-3 mx-auto w-24"></div>
                <div className="h-4 bg-gray-300 rounded mb-4 mx-auto w-28"></div>
                <div className="flex justify-center space-x-3">
                  <div className="w-6 h-6 bg-gray-300 rounded"></div>
                  <div className="w-6 h-6 bg-gray-300 rounded"></div>
                  <div className="w-6 h-6 bg-gray-300 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="team" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-950 rounded-full text-sm font-medium mb-4">
            <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
            Meet Our Experts
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-purple-600">Professional Team</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Meet the dedicated professionals who make your travel dreams and
            financial goals come true
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {teamMembers?.slice(0, 6).map((member: any) => (
            <div key={member.id} className="text-center group">
              <div className="relative mb-8">
                <div className="relative w-48 h-48 mx-auto">
                  <img
                    src={`${member.image || member.imageUrl}?cache=${Date.now()}`}
                    alt={member.name}
                    width={192}
                    height={192}
                    className="w-full h-full rounded-3xl object-cover shadow-2xl group-hover:shadow-3xl transition-all duration-300"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-600/20 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                {member.name}
              </h3>
              <p className="text-orange-600 font-semibold mb-3 text-lg text-center">
                {member.position}
              </p>
              <p className="text-gray-600 mb-6 max-w-xs mx-auto text-center">
                {member.experience}
              </p>
              <div className="flex justify-center space-x-3">
                {member.email && (
                  <a
                    href={`mailto:${member.email}`}
                    className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 hover:bg-orange-200 transition-colors group-hover:scale-110 transform duration-300"
                    title={`Email ${member.name}`}
                    aria-label={`Email ${member.name}`}
                  >
                    <Mail className="w-6 h-6" aria-hidden />
                  </a>
                )}
                <div className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
                  {member.specialization}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="/team"
            className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            View Full Team
            <svg
              className="ml-2 w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
